import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { ApiOrchestrator } from 'kiota/services/api.orchestrator';
import { BehaviorSubject, from, map, Observable } from 'rxjs';
import { ImpersonationTokenHolder } from './impersonation-token.holder';
import { PermissionService } from './permission.service';

const STORAGE_KEY = 'impersonation';

export interface ImpersonationState {
    userId: string;
    username: string;
    firstName: string;
    lastName: string;
    email: string;
    roles: string[];
    accessToken: string;
    refreshToken: string | null;
    expiresIn: number;
}

@Injectable({
    providedIn: 'root',
})
export class ImpersonationService {
    private _state = new BehaviorSubject<ImpersonationState | null>(null);
    state$ = this._state.asObservable();

    constructor(
        private _apiOrchestrator: ApiOrchestrator,
        private _permissionService: PermissionService,
        private _navigationService: NavigationService,
        private _router: Router
    ) {
        // Restore from sessionStorage on init (survives page reload)
        const stored = sessionStorage.getItem(STORAGE_KEY);
        if (stored) {
            try {
                const state = JSON.parse(stored) as ImpersonationState;
                this._state.next(state);
                ImpersonationTokenHolder.token = state.accessToken;
                // Reload permissions from the stored token
                this._permissionService.loadFromRawToken(state.accessToken);
            } catch {
                sessionStorage.removeItem(STORAGE_KEY);
            }
        }
    }

    get isImpersonating(): boolean {
        return this._state.value !== null;
    }

    get impersonatedUserId(): string | null {
        return this._state.value?.userId ?? null;
    }

    get currentState(): ImpersonationState | null {
        return this._state.value;
    }

    /** Returns the impersonated access token, or null if not impersonating. */
    get impersonatedToken(): string | null {
        return this._state.value?.accessToken ?? null;
    }

    startImpersonation(userId: string): Observable<ImpersonationState> {
        return from(this._apiOrchestrator.client.api.v1.users.byUserId(userId).impersonate.post()).pipe(
            map((result) => {
                if (!result) throw new Error('Impersonation failed: empty response');
                const state: ImpersonationState = {
                    userId: result.id ?? '',
                    username: result.username ?? '',
                    firstName: result.firstName ?? '',
                    lastName: result.lastName ?? '',
                    email: result.email ?? '',
                    roles: result.roles ?? [],
                    accessToken: result.accessToken ?? '',
                    refreshToken: result.refreshToken ?? null,
                    expiresIn: result.expiresIn ?? 0,
                };

                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
                this._state.next(state);
                ImpersonationTokenHolder.token = state.accessToken;

                // Parse the impersonated JWT to load permissions
                this._permissionService.loadFromRawToken(state.accessToken);

                // Rebuild navigation with new permissions
                this._navigationService.rebuild();

                return state;
            })
        );
    }

    stopImpersonation(): void {
        sessionStorage.removeItem(STORAGE_KEY);
        this._state.next(null);
        ImpersonationTokenHolder.token = null;

        // Reload permissions from the real Keycloak token
        this._permissionService.loadFromToken();

        // Rebuild navigation with restored permissions
        this._navigationService.rebuild();

        // Navigate to dashboard
        this._router.navigate(['/']);
    }
}
